<template>
  <div class="asset-view-wrapper">
    <div class="assets-table-container">
      <div
        v-if="!listLoading"
        class="wl-kpis"
      >
        <!-- dark — aktywne assety łącznie -->
        <div class="wl-stat wl-stat--dark">
          <div class="wl-stat__head">
            <span class="wl-stat__title">{{ $t('components.wirepasLocations.kpis.activeAssets') }}</span>
            <i class="bi bi-broadcast-pin wl-stat__icon" />
          </div>
          <span class="wl-stat__value">{{ totalAssetCount }}</span>
        </div>

        <!-- aktywne lokalizacje (online), z paskiem accentu -->
        <div class="wl-stat">
          <div class="wl-stat__head">
            <span class="wl-stat__title">{{ $t('components.wirepasLocations.kpis.activeLocations') }}</span>
            <i class="bi bi-geo-alt wl-stat__icon" />
          </div>
          <span class="wl-stat__value">{{ onlineCount }}</span>
          <div
            class="wl-stat__accent"
            :style="{ width: listData.length ? `${(onlineCount / listData.length) * 100}%` : '0%' }"
          />
        </div>

        <!-- palety -->
        <div class="wl-stat">
          <div class="wl-stat__head">
            <span class="wl-stat__title">{{ $t('components.wirepasLocations.columns.pallet') }}</span>
            <i class="bi bi-box-seam wl-stat__icon wl-stat__icon--pallet" />
          </div>
          <span class="wl-stat__value">{{ totalCounts.pallet }}</span>
        </div>

        <!-- wózki -->
        <div class="wl-stat">
          <div class="wl-stat__head">
            <span class="wl-stat__title">{{ $t('components.wirepasLocations.columns.cart') }}</span>
            <i class="bi bi-cart3 wl-stat__icon wl-stat__icon--cart" />
          </div>
          <span class="wl-stat__value">{{ totalCounts.cart }}</span>
        </div>

        <!-- boxy -->
        <div class="wl-stat">
          <div class="wl-stat__head">
            <span class="wl-stat__title">{{ $t('components.wirepasLocations.columns.box') }}</span>
            <i class="bi bi-archive wl-stat__icon wl-stat__icon--box" />
          </div>
          <span class="wl-stat__value">{{ totalCounts.box }}</span>
        </div>

        <!-- tagi -->
        <div class="wl-stat">
          <div class="wl-stat__head">
            <span class="wl-stat__title">{{ $t('components.wirepasLocations.columns.tag') }}</span>
            <i class="bi bi-tag wl-stat__icon wl-stat__icon--tag" />
          </div>
          <span class="wl-stat__value">{{ totalCounts.tag }}</span>
        </div>

        <!-- wszystkie dostępne (suma) -->
        <div class="wl-stat">
          <div class="wl-stat__head">
            <span class="wl-stat__title">{{ $t('components.wirepasLocations.kpis.allAssets') }}</span>
            <i class="bi bi-collection wl-stat__icon" />
          </div>
          <span class="wl-stat__value">{{ totalAssetCount }}</span>
          <span class="wl-stat__meta">{{ $t('components.wirepasLocations.kpis.allAssetsMeta') }}</span>
        </div>
      </div>

      <div class="assets-grid-card">
        <div class="assets-grid-card__header">
          <div class="assets-grid-title">
            <div class="assets-grid-title__icon">
              <i class="bi bi-broadcast-pin" />
            </div>
            <div>
              <div class="assets-grid-title__text">
                {{ $t('aside.menu.pages.wirepasLocations') }}
              </div>
              <div class="text-muted small">
                {{ $t('components.wirepasLocations.subtitle') }}
              </div>
            </div>
          </div>

          <div class="assets-grid-controls">
            <button
              type="button"
              class="assets-toolbar-btn"
              @click="openCreateModal"
            >
              <i class="bi bi-plus-circle" />
              <span>{{ $t('components.wirepasLocations.addButton') }}</span>
            </button>
            <button
              type="button"
              class="assets-toolbar-btn"
              :title="$t('components.assets.dataGrids.refresh')"
              @click="fetchList"
            >
              <i class="bi bi-arrow-clockwise" />
              <span>{{ $t('components.assets.dataGrids.refresh') }}</span>
            </button>
          </div>
        </div>

        <div class="assets-grid-card__body">
          <div
            v-if="listError"
            class="alert alert-danger d-flex align-items-center gap-2 mb-3"
          >
            <i class="bi bi-exclamation-triangle-fill" />
            <span>{{ listError }}</span>
          </div>
          <div
            v-if="listLoading"
            class="wp-loading"
          >
            <span
              class="spinner-border wp-loading__spinner"
              role="status"
            />
            <span class="wp-loading__text">{{ $t('components.wirepasLocations.loading') }}</span>
          </div>
          <div v-else>
            <DxDataGrid
              class="wirepas-locations-grid"
              :data-source="listData"
              :hover-state-enabled="true"
              :column-auto-width="false"
              :show-borders="false"
              :show-column-lines="false"
              :show-row-lines="false"
              :no-data-text="$t('components.wirepasLocations.empty')"
              :row-alternation-enabled="true"
              :column-hiding-enabled="true"
              :word-wrap-enabled="true"
            >
              <DxHeaderFilter :visible="true" />
              <DxFilterRow :visible="true" />
              <DxPaging :page-size="20" />
              <DxPager
                :allowed-page-sizes="[10, 20, 50]"
                :show-info="true"
                :show-page-size-selector="true"
                :show-navigation-buttons="true"
              />
              <DxMasterDetail
                :enabled="true"
                template="location-detail"
              />

              <DxColumn
                data-field="online"
                :caption="$t('components.wirepasLocations.columns.status')"
                width="120"
                alignment="center"
                cell-template="status-template"
                :allow-filtering="true"
              />
              <DxColumn
                data-field="name"
                :caption="$t('components.wirepasLocations.columns.name')"
                :min-width="180"
                cell-template="name-template"
              />
              <DxColumn
                data-field="addressText"
                :caption="$t('components.wirepasLocations.columns.address')"
                :min-width="180"
                :allow-filtering="false"
                :hiding-priority="1"
              />
              <DxColumn
                data-field="typeLabel"
                :caption="$t('components.wirepasLocations.columns.type')"
                width="120"
                alignment="center"
                cell-template="type-template"
                :allow-filtering="true"
                :hiding-priority="4"
              />
              <DxColumn
                data-field="countPallet"
                :caption="$t('components.wirepasLocations.columns.pallet')"
                width="90"
                alignment="center"
                :allow-filtering="false"
                :allow-sorting="true"
                :hiding-priority="5"
                cell-template="count-pallet-template"
              />
              <DxColumn
                data-field="countCart"
                :caption="$t('components.wirepasLocations.columns.cart')"
                width="90"
                alignment="center"
                :allow-filtering="false"
                :hiding-priority="6"
                cell-template="count-cart-template"
              />
              <DxColumn
                data-field="countBox"
                :caption="$t('components.wirepasLocations.columns.box')"
                width="90"
                alignment="center"
                :allow-filtering="false"
                :hiding-priority="7"
                cell-template="count-box-template"
              />
              <DxColumn
                data-field="countTag"
                :caption="$t('components.wirepasLocations.columns.tag')"
                width="90"
                alignment="center"
                :allow-filtering="false"
                :hiding-priority="8"
                cell-template="count-tag-template"
              />
              <DxColumn
                data-field="assetCount"
                :caption="$t('components.wirepasLocations.columns.total')"
                width="90"
                alignment="center"
                :allow-filtering="false"
                :hiding-priority="3"
              />
              <DxColumn
                data-field="gatewayCount"
                :caption="$t('components.wirepasLocations.columns.gateways')"
                width="90"
                alignment="center"
                :allow-filtering="false"
                :hiding-priority="2"
              />
              <DxColumn
                :caption="$t('components.assetGroupsWithLocation.columns.actions')"
                width="140"
                :allow-filtering="false"
                :allow-sorting="false"
                cell-template="actions-template"
              />

              <template #status-template="{ data }">
                <span
                  class="wirepas-status-badge"
                  :class="data.data.online ? 'wirepas-status-badge--online' : 'wirepas-status-badge--offline'"
                >
                  <span
                    class="wirepas-status-dot"
                    :class="data.data.online ? 'wirepas-status-dot--online' : 'wirepas-status-dot--offline'"
                  />
                  {{ data.data.online ? $t('components.wirepasLocations.online') : $t('components.wirepasLocations.offline') }}
                </span>
              </template>

              <template #actions-template="{ data }">
                <div class="wl-row-actions">
                  <button
                    type="button"
                    class="wl-row-action"
                    :title="$t('components.wirepasLocations.detail.showDetail')"
                    @click.stop="$router.push(`/wirepas-locations/${data.data.id}/detail`)"
                  >
                    <i class="bi bi-eye" />
                  </button>
                  <button
                    v-if="data.data.schemeId"
                    type="button"
                    class="wl-row-action"
                    :title="$t('components.wirepasLocations.showScheme')"
                    @click.stop="$router.push({ name: 'AssetSchemePreview', params: { id: data.data.schemeId } })"
                  >
                    <i class="bi bi-diagram-3" />
                  </button>
                  <button
                    v-if="canManageLocations"
                    type="button"
                    class="wl-row-action"
                    :title="$t('components.assetGroupsWithLocation.actions.edit')"
                    @click.stop="openEditModal(data.data)"
                  >
                    <i class="bi bi-pencil" />
                  </button>
                  <button
                    v-if="canManageLocations"
                    type="button"
                    class="wl-row-action wl-row-action--danger"
                    :title="$t('components.wirepasLocations.deleteLocation')"
                    :disabled="deletingId === data.data.id"
                    @click.stop="confirmDelete(data.data)"
                  >
                    <i
                      v-if="deletingId === data.data.id"
                      class="spinner-border spinner-border-sm"
                    />
                    <i
                      v-else
                      class="bi bi-trash"
                    />
                  </button>
                </div>
              </template>

              <template #type-template="{ data }">
                <span
                  v-if="data.data.typeLabel"
                  class="wirepas-type-badge"
                  :class="`wirepas-type-badge--${data.data.type}`"
                >{{ data.data.typeLabel }}</span>
                <span
                  v-else
                  class="text-muted small"
                >—</span>
              </template>

              <template #count-pallet-template="{ data }">
                <span :class="data.data.countPallet ? 'wirepas-count wirepas-count--pallet' : 'text-muted'">{{ data.data.countPallet || '–' }}</span>
              </template>
              <template #count-cart-template="{ data }">
                <span :class="data.data.countCart ? 'wirepas-count wirepas-count--cart' : 'text-muted'">{{ data.data.countCart || '–' }}</span>
              </template>
              <template #count-box-template="{ data }">
                <span :class="data.data.countBox ? 'wirepas-count wirepas-count--box' : 'text-muted'">{{ data.data.countBox || '–' }}</span>
              </template>
              <template #count-tag-template="{ data }">
                <span :class="data.data.countTag ? 'wirepas-count wirepas-count--tag' : 'text-muted'">{{ data.data.countTag || '–' }}</span>
              </template>

              <template #name-template="{ data }">
                <span class="d-flex align-items-center gap-2">
                  <span class="fw-semibold">{{ data.data.name }}</span>
                  <a
                    v-if="data.data.mapsUrl"
                    :href="data.data.mapsUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="wirepas-map-link"
                    :title="$t('components.wirepasLocations.navigate')"
                    @click.stop
                  >
                    <i class="bi bi-geo-alt" />
                  </a>
                </span>
              </template>

              <template #location-detail="{ data }">
                <div class="wirepas-location-detail">
                  <div class="wirepas-location-detail__section">
                    <strong>{{ $t('components.wirepasLocations.detail.gateways') }}</strong>
                    <ul class="list-unstyled mb-0">
                      <li
                        v-for="gw in data.data.gateways"
                        :key="gw.gwId"
                        class="mb-1"
                      >
                        <span
                          class="wirepas-status-dot"
                          :class="gw.state === 'online' ? 'wirepas-status-dot--online' : 'wirepas-status-dot--offline'"
                        />
                        {{ gw.gwId }}
                        <span
                          v-if="gw.gatewayModel"
                          class="text-muted small"
                        >
                          ({{ gw.gatewayModel }} {{ gw.gatewayVersion }})
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div class="wirepas-location-detail__section">
                    <strong>{{ $t('components.wirepasLocations.detail.countsByModel') }}</strong>
                    <ul class="list-unstyled mb-0">
                      <li
                        v-for="(count, modelName) in data.data.countsByDeviceModel"
                        :key="modelName"
                        class="mb-1 d-flex align-items-center"
                      >
                        <span
                          class="wirepas-model-chip"
                          :style="{ background: modelColor(String(modelName)) }"
                        />
                        <span style="white-space: nowrap;">{{ modelName }}: <span style="font-weight: 700;">{{ count }}</span></span>
                      </li>
                      <li v-if="!Object.keys(data.data.countsByDeviceModel).length">
                        {{ $t('components.wirepasLocations.detail.noAssets') }}
                      </li>
                    </ul>
                  </div>
                </div>
              </template>
            </DxDataGrid>
          </div>
        </div>
      </div>
    </div>

    <WirepasLocationFormModal
      ref="formModal"
      @saved="onLocationSaved"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useRouter } from 'vue-router'
import {
  DxDataGrid,
  DxColumn,
  DxFilterRow,
  DxHeaderFilter,
  DxPaging,
  DxPager,
  DxMasterDetail
} from 'devextreme-vue/data-grid'
import { getWirepasLocations, getWirepasLocation } from '@/crud/wirepas-location'
import { deleteWirepasLocation } from '@/crud/wirepas-location'
import WirepasLocationFormModal from '@/components/content/_modals/WirepasLocationFormModal.vue'
import { canAccessInfrastructure } from '@/utils/tools'

const LOCATION_TYPE_LABELS: Record<string, string> = {
  hala: 'Hala',
  crossdock: 'Cross-dock',
  sklep: 'Sklep',
}

type LocationRow = {
  id: number
  assetGroupId: number | null
  name: string
  type: string | null
  typeLabel: string | null
  addressText: string
  mapsUrl: string | null
  online: boolean
  gatewayCount: number
  assetCount: number
  countPallet: number
  countCart: number
  countBox: number
  countTag: number
  gateways: any[]
  countsByDeviceModel: Record<string, number>
}

const MODEL_COLORS = ['#a9801e', '#4870b8', '#3fa64a', '#8f5fd9', '#d9714e', '#2ea1a1']

export default defineComponent({
  name: 'WirepasLocationsView',
  components: {
    DxDataGrid,
    DxColumn,
    DxFilterRow,
    DxHeaderFilter,
    DxPaging,
    DxPager,
    DxMasterDetail,
    WirepasLocationFormModal
  },
  emits: ['pageName'],
  data() {
    return {
      listData: [] as LocationRow[],
      listLoading: false,
      listError: null as string | null,
      deletingId: null as number | null,
    }
  },
  computed: {
    onlineCount(): number {
      return this.listData.filter((l) => l.online).length
    },
    totalAssetCount(): number {
      return this.listData.reduce((sum, l) => sum + l.assetCount, 0)
    },
    totalCounts(): Record<string, number> {
      return {
        pallet: this.listData.reduce((s, l) => s + l.countPallet, 0),
        cart:   this.listData.reduce((s, l) => s + l.countCart, 0),
        box:    this.listData.reduce((s, l) => s + l.countBox, 0),
        tag:    this.listData.reduce((s, l) => s + l.countTag, 0),
      }
    },
    // Same role bar as the /infrastructure page -- the backend already enforces this on
    // edit/delete (AssetGroupVoter::EDIT plus, for delete, an explicit matching IsGranted), this
    // just keeps the buttons from being shown to a user who'd get a 403 clicking them.
    canManageLocations(): boolean {
      return canAccessInfrastructure()
    }
  },
  watch: {
    '$i18n.locale'() {
      this.setTitle()
    }
  },
  mounted() {
    this.setTitle()
    this.fetchList()
  },
  methods: {
    setTitle() {
      this.$emit('pageName', this.$t('aside.menu.pages.wirepasLocations'))
    },

    openCreateModal() {
      (this.$refs.formModal as any)?.openModal()
    },

    async onLocationSaved(isEdit: boolean) {
      await this.fetchList();
      (this as any).$toast.success(
        isEdit
          ? this.$t('components.wirepasLocations.toast.updated')
          : this.$t('components.wirepasLocations.toast.created')
      )
    },

    async confirmDelete(row: LocationRow) {
      const result = await (this as any).$swal.fire({
        icon: 'warning',
        title: this.$t('components.wirepasLocations.deleteConfirm.title'),
        text: this.$t('components.wirepasLocations.deleteConfirm.text', { name: row.name }),
        showCancelButton: true,
        confirmButtonText: this.$t('components.wirepasLocations.deleteConfirm.confirm'),
        cancelButtonText: this.$t('components.wirepasLocations.deleteConfirm.cancel'),
        confirmButtonColor: '#dc3545',
      })
      if (!result.isConfirmed) return

      this.deletingId = row.id
      try {
        await deleteWirepasLocation(row.id)
        await this.fetchList();
        (this as any).$toast.success(this.$t('components.wirepasLocations.deleteConfirm.success', { name: row.name }))
      } catch (e: any) {
        (this as any).$toast.error(e?.response?.data?.error || this.$t('components.wirepasLocations.deleteConfirm.error'))
      } finally {
        this.deletingId = null
      }
    },

    async openEditModal(row: LocationRow) {
      try {
        const res = await getWirepasLocation(row.id)
        const data = res.data?.data
        if (data) {
          ;(this.$refs.formModal as any)?.openModal(data)
        }
      } catch (e: any) {
        this.listError = e?.message || this.$t('components.wirepasLocations.error')
      }
    },

    modelColor(modelName: string): string {
      let hash = 0
      for (let i = 0; i < modelName.length; i++) {
        hash = (hash * 31 + modelName.charCodeAt(i)) % MODEL_COLORS.length
      }
      return MODEL_COLORS[Math.abs(hash) % MODEL_COLORS.length]
    },

    async fetchList() {
      this.listLoading = true
      this.listError = null
      try {
        const response = await getWirepasLocations()
        const locations = response.data?.data || []

        this.listData = locations.map((location: any) => {
          const addressParts = [
            location.address?.street,
            location.address?.city,
            location.address?.country
          ].filter(Boolean)

          const lat = location.lat
          const lng = location.lng
          const mapsUrl = lat && lng
            ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
            : (addressParts.length
              ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addressParts.join(', '))}`
              : null)

          const cats = location.countsByCategory ?? {}
          return {
            id: location.id,
            assetGroupId: location.assetGroupId ?? null,
            schemeId: location.schemeId ?? null,
            name: location.name,
            type: location.type ?? null,
            typeLabel: location.type ? (LOCATION_TYPE_LABELS[location.type] ?? location.type) : null,
            addressText: addressParts.join(', ') || '-',
            mapsUrl,
            online: !!location.online,
            gatewayCount: location.gateways?.length ?? 0,
            assetCount: location.assetCount ?? 0,
            countPallet: cats.pallet ?? 0,
            countCart: cats.cart ?? 0,
            countBox: cats.box ?? 0,
            countTag: cats.tag ?? 0,
            gateways: location.gateways || [],
            countsByDeviceModel: location.countsByDeviceModel || {}
          }
        })
      } catch (e: any) {
        this.listError = e?.response?.data?.error || e?.message || this.$t('components.wirepasLocations.error')
      } finally {
        this.listLoading = false
      }
    }
  }
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/wirepas-locations-view.scss';
</style>
